import { MainThunkDispatch, State } from "../../../Reducers"
import { InstanceAPIType } from "../../Types"
import { failMessage, successMessage } from "../../Client/MessagesActions"
import { safePromise } from "../../../Helpers/Functions/promise"
import { apiFetch } from "../../Client/NetworkActions"
import { apiEndpoints } from "../../../Routes/api"
import {
    APIGroundTruthModel,
    APIGroundTruthType,
    APIGroundTruthDetailedModel
} from "../../../Models/API/APIGroundTruthModel"
import { FilterAndSortExpression } from "../../../Models/Client/FilterAndSortExpression"
import { hasMore } from "../../../Models/Client/RemoteData"
import { ConstFilterOperator } from "../../../Models/Client/FilterExpression"
import { APIListContainer } from "../../../Models/API/Container"
import { uploadData } from "../Data/DataActions"
import { randID } from "../../../Helpers/Functions/random"
import { embedReference } from "universal-schema/lib"
import { JSONSchema7 } from "json-schema"

function createUploader(
    uploadPathPrefix: string,
    dispatch: MainThunkDispatch
): (blob: Blob) => Promise<string> {
    return (blob: Blob) => {
        const path = `${uploadPathPrefix}/${randID()}.json`

        return dispatch(uploadData(blob, path))
    }
}

export const getGroundTruth =
    (instanceID: string, baselineID: string) =>
    async (dispatch: MainThunkDispatch) => {
        const { data, error } = await safePromise(
            dispatch(
                apiFetch<APIGroundTruthDetailedModel>(
                    `GET_GROUND_TRUTH_${baselineID}`,
                    apiEndpoints.baseline({ id: baselineID })
                )
            )
        )

        if (error !== undefined) {
            dispatch({
                type: InstanceAPIType.baselineData_setError,
                meta: {
                    appID: instanceID,
                    groundTruthID: baselineID
                },
                payload: error.message
            })
            return
        }

        if (data === undefined) {
            return
        }

        dispatch({
            type: InstanceAPIType.baselineData_setOne,
            payload: {
                instanceID,
                baselineID,
                baselineData: data
            }
        })
    }

export const getGroundTruthTotalCount =
    (appID: string) => async (dispatch: MainThunkDispatch) => {
        const { data, error } = await safePromise(
            dispatch(
                apiFetch<APIListContainer<APIGroundTruthModel>>(
                    `GET_BASELINE_TOTAL_COUNT_${appID}}`,
                    apiEndpoints.baselineDataList({
                        appID,
                        page: 0,
                        pageSize: 1,
                        sort: { sortBy: "created", desc: false }
                    })
                )
            )
        )

        if (error !== undefined || data === undefined) {
            return
        }

        dispatch({
            type: InstanceAPIType.baselineData_setTotalCount,
            meta: {
                appID
            },
            payload: data.totalElements
        })
    }

export const getGroundTruthList =
    (
        appID: string,
        page: number = 0,
        pageSize: number = 20,
        filterAndSort: FilterAndSortExpression<APIGroundTruthModel>
    ) =>
    async (dispatch: MainThunkDispatch) => {
        const { data, error } = await safePromise(
            dispatch(
                apiFetch(
                    `GET_BASELINE_LIST_${JSON.stringify({
                        appID,
                        page,
                        pageSize,
                        filterAndSort
                    })}}`,
                    apiEndpoints.baselineDataList({
                        appID,
                        page,
                        pageSize,
                        sort: filterAndSort.sort,
                        type: filterAndSort.filter.reduce(
                            (passed, f) =>
                                f.fieldName === "type" &&
                                f.operator === ConstFilterOperator.eq
                                    ? [f.value]
                                    : f.operator === ConstFilterOperator.isOneOf
                                    ? f.value
                                    : passed,
                            undefined as undefined | APIGroundTruthType[]
                        )
                    })
                )
            )
        )

        if (error !== undefined || data === undefined) {
            return
        }

        dispatch({
            type: InstanceAPIType.baselineData_setList,
            meta: {
                total: data.totalElements,
                page,
                params: filterAndSort
            },
            payload: {
                appID,
                baselineData: data.content
            }
        })
    }

export const loadMoreGroundTruthList =
    (appID: string) =>
    async (dispatch: MainThunkDispatch, getState: () => State) => {
        const table = getState().api.baselineData.table[appID]

        if (table !== undefined && table.list !== undefined && hasMore(table)) {
            await dispatch(
                getGroundTruthList(
                    appID,
                    table.list.pages.length,
                    undefined,
                    table.list.params
                )
            )
        }
    }

export const createFromJob =
    (appID: string, jobID: number) => async (dispatch: MainThunkDispatch) => {
        const { data, error } = await safePromise(
            dispatch(
                apiFetch(
                    `CREATE_BASELINE_FROM_JOB_${jobID}`,
                    apiEndpoints.baselineDataFromJob({ appID, jobID }),
                    {
                        method: "POST"
                    }
                )
            )
        )

        if (error !== undefined || data === undefined) {
            if (error !== undefined)
                dispatch(failMessage("Could not add to ground truth", error))
            return
        }

        dispatch({
            type: InstanceAPIType.baselineData_setList,
            payload: {
                appID,
                baselineData: data.content
            }
        })

        dispatch(
            successMessage(
                `Ground truth data successfully created from data point ${jobID}`
            )
        )
    }

export const createBaselineFromEntries =
    (
        appID: string,
        inputFormID: string,
        outputFormID: string,
        inputSchema: JSONSchema7,
        outputSchema: JSONSchema7
    ) =>
    async (dispatch: MainThunkDispatch, getState: () => State) => {
        const inputs = getState().client.inputRender.entries[inputFormID]
        const outputs = getState().client.inputRender.entries[outputFormID]
        await dispatch(
            createBaselineData(
                appID,
                inputs?.values,
                outputs?.values,
                "ground-truth/new/input",
                "ground-truth/new/label",
                inputSchema,
                outputSchema,
                false
            )
        )
    }

export const createBaselineData =
    (
        appID: string,
        input: unknown,
        output: unknown,
        inputUploadPathPrefix: string,
        outputUploadPathPrefix: string,
        inputSchema: JSONSchema7,
        outputSchema: JSONSchema7,
        squashMessage: boolean
    ) =>
    async (dispatch: MainThunkDispatch) => {
        const convertedInput = await embedReference(
            input,
            inputSchema,
            createUploader(inputUploadPathPrefix, dispatch)
        )
        const convertedLabel = await embedReference(
            output,
            outputSchema,
            createUploader(outputUploadPathPrefix, dispatch)
        )

        return new Promise(async (resolve, reject) => {
            const { data, error } = await safePromise(
                dispatch(
                    apiFetch(
                        `CREATE_BASELINE_LIST_${appID}`,
                        apiEndpoints.baselineData({ appID }),
                        {
                            method: "POST",
                            body: JSON.stringify({
                                input: convertedInput,
                                label: convertedLabel
                            })
                        },
                        0
                    )
                )
            )

            if (error !== undefined || data === undefined) {
                if (error !== undefined && !squashMessage)
                    dispatch(
                        failMessage("Could not create ground truth", error)
                    )
                return reject((error && error.message) || "")
            }

            dispatch({
                type: InstanceAPIType.baselineData_setOne,
                payload: {
                    instanceID: appID,
                    baselineID: data.id,
                    baselineData: data
                }
            })

            if (!squashMessage) {
                dispatch(successMessage("Ground truth data is created"))
            }
            return resolve(data)
        })
    }

export const batchCreateBaselineData =
    (
        appID: string,
        data: { input: any; output: any }[],
        inputUploadPathPrefix: string,
        labelUploadPathPrefix: string,
        inputSchema: JSONSchema7,
        outputSchema: JSONSchema7
    ) =>
    async (dispatch: MainThunkDispatch) => {
        // Give feedback first if the action is likely to take some time
        // We can remove this when we implement loaders etc. to tell intermediate state.
        if (data.length > 1) {
            dispatch(successMessage("Started to create ground truth data"))
        }

        await data
            .reduce(
                (chain, item) =>
                    chain.then(res =>
                        dispatch(
                            createBaselineData(
                                appID,
                                item.input,
                                item.output,
                                inputUploadPathPrefix,
                                labelUploadPathPrefix,
                                inputSchema,
                                outputSchema,
                                true
                            )
                        )
                            .then(_ => res.concat(true))
                            .catch(error => res.concat(error))
                    ),
                Promise.resolve([] as any[])
            )
            .then(res => {
                const errors = res.filter(item => item !== true && item !== "")
                if (errors.length > 0) {
                    dispatch(
                        failMessage(
                            "Could not create all ground truth",
                            errors[0]
                        )
                    )
                } else {
                    dispatch(successMessage("Ground truth is created"))
                }
            })
    }

export const updateBaselineData =
    (
        instanceID: string,
        baselineID: string,
        output: unknown,
        uploadPathPrefix: string,
        outputSchema: JSONSchema7,
        squashMessage?: boolean
    ) =>
    async (dispatch: MainThunkDispatch) => {
        const convertedLabel = await embedReference(
            output,
            outputSchema,
            createUploader(uploadPathPrefix, dispatch)
        )
        const { data, error } = await safePromise<APIGroundTruthDetailedModel>(
            dispatch(
                apiFetch(
                    `UPDATE_BASELINE_${baselineID}`,
                    apiEndpoints.baseline({ id: baselineID }),
                    {
                        method: "PATCH",
                        body: JSON.stringify({
                            label: convertedLabel
                        })
                    }
                )
            )
        )

        if (error !== undefined || data === undefined) {
            if (error !== undefined && !squashMessage)
                dispatch(failMessage("Could not update ground truth", error))
            throw Error((error && error.message) || "")
        }

        dispatch({
            type: InstanceAPIType.baselineData_setOne,
            payload: {
                instanceID,
                baselineID,
                baselineData: data
            }
        })

        if (!squashMessage)
            dispatch(successMessage("Ground truth data was updated"))
    }
